import { ArrowDown, Bitcoin, MapPin, Zap } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { SITE } from '@/lib/site';

export function Hero() {
  const author = useAuthor(SITE.ownerPubkey);
  const metadata = author.data?.metadata;
  const avatarUrl = metadata?.picture;
  const initials = SITE.name.split(' ').map((w) => w[0]).join('');

  return (
    <section id="top" className="relative isolate overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10" aria-hidden="true">
        <div className="absolute -top-32 -left-32 size-96 rounded-full bg-bitcoin/20 blur-3xl" />
        <div className="absolute top-1/3 -right-32 size-96 rounded-full bg-nostr/20 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35] dark:opacity-[0.15]"
          style={{
            backgroundImage:
              'linear-gradient(var(--color-border) 1px, transparent 1px), linear-gradient(90deg, var(--color-border) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent)',
          }}
        />
      </div>

      <div className="container py-20 md:py-28 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-[1.4fr_1fr] lg:gap-16">
          {/* Text column */}
          <div className="space-y-8">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="gap-1.5 rounded-full px-3 py-1 text-xs font-medium">
                <MapPin className="size-3" />
                {SITE.location}
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-full px-3 py-1 text-xs font-medium border-bitcoin/40 text-bitcoin">
                <Bitcoin className="size-3" />
                Bitcoin
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-full px-3 py-1 text-xs font-medium border-nostr/40 text-nostr">
                <Zap className="size-3" />
                Nostr
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
                {SITE.tagline.split('Bitcoin')[0]}
                <span className="text-bitcoin">Bitcoin</span>
                {' & '}
                <span className="text-nostr">Nostr</span>
              </h1>
              <p className="max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                Hi, I'm {SITE.name} — an open source developer crafting
                permissionless tools from Nairobi, for the world.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-full font-semibold">
                <a href="#work">
                  See my work
                  <ArrowDown className="size-4" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full font-semibold">
                <a href="#notes">What I'm up to</a>
              </Button>
            </div>

            <div className="flex items-center gap-2 font-mono text-sm text-muted-foreground">
              <span className="text-bitcoin">$</span>
              <span>currently shipping open source</span>
              <span className="inline-block h-4 w-2 animate-pulse bg-bitcoin" aria-hidden="true" />
            </div>
          </div>

          {/* Avatar column */}
          <div className="relative mx-auto w-full max-w-xs lg:max-w-sm">
            <div className="relative isolate aspect-square">
              <div className="absolute inset-0 -z-10 rounded-full bg-gradient-to-br from-bitcoin/40 via-transparent to-nostr/40 blur-2xl" aria-hidden="true" />
              <div className="absolute inset-4 rounded-full border border-dashed border-bitcoin/30 motion-safe:animate-[spin_40s_linear_infinite]" aria-hidden="true" />
              <div className="absolute inset-0 rounded-full border border-nostr/20 motion-safe:animate-[spin_60s_linear_infinite_reverse]" aria-hidden="true" />

              <div className="absolute inset-8 overflow-hidden rounded-full border-4 border-card bg-muted shadow-2xl ring-1 ring-border">
                {author.isLoading ? (
                  <Skeleton className="size-full rounded-full" />
                ) : (
                  <Avatar className="size-full">
                    <AvatarImage src={avatarUrl} alt={SITE.name} className="object-cover" />
                    <AvatarFallback className="bg-gradient-to-br from-bitcoin to-nostr font-display text-5xl font-bold text-white">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>

              {/* Floating badges */}
              <div className="absolute -right-2 top-10 flex size-14 items-center justify-center rounded-2xl border border-border bg-card shadow-lg motion-safe:animate-[bounce_5s_ease-in-out_infinite]" aria-hidden="true">
                <Bitcoin className="size-7 text-bitcoin" />
              </div>
              <div className="absolute -left-2 bottom-10 flex size-14 items-center justify-center rounded-2xl border border-border bg-card shadow-lg motion-safe:animate-[bounce_6s_ease-in-out_infinite]" aria-hidden="true">
                <Zap className="size-7 text-nostr" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
